import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button, ConfirmDeleteDialog, Field, Input, PasswordInput } from "./ui-kit";

// Copia nel campo l'email inserita al passo "Azienda" (es. per il titolare,
// la cui email e' anche l'email aziendale usata dagli addetti con il PIN).
export function CopyCompanyEmailButton({ companyEmail, onCopy, testid, disabledReason }) {
  const email = (companyEmail || "").trim();
  const blocked = disabledReason || (!email && "Nessuna email inserita nei dati dell’azienda");
  return (
    <Button type="button" variant="outline" className="h-auto shrink-0 px-3 text-xs" disabled={Boolean(blocked)}
      title={blocked || `Copia ${email}`}
      onClick={() => { onCopy(email); toast.success("Email copiata dall’azienda"); }} data-testid={testid}>
      <Copy className="h-4 w-4" /> Copia da azienda
    </Button>
  );
}

// Due schede separate per l'area utenti: titolare/responsabili e addetti non
// vengono mai mostrati insieme, per non confondere i due tipi di accesso.
export function UserSectionTabs({ value, onChange, ownersCount, workersCount, testid = "user-section-tabs" }) {
  const tabs = [
    { key: "owners", label: "Titolare e responsabili", hint: "Email e password", count: ownersCount },
    { key: "workers", label: "Addetti", hint: "Email aziendale + PIN", count: workersCount },
  ];
  return (
    <div role="tablist" aria-label="Tipo di utente" className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1.5" data-testid={testid}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button key={tab.key} type="button" role="tab" aria-selected={active} onClick={() => onChange(tab.key)}
            className={`min-h-[56px] rounded-xl px-3 py-2 text-left transition-colors ${active ? (tab.key === "workers" ? "bg-emerald-600 text-white shadow" : "bg-navy text-white shadow") : "text-slate-600 hover:bg-white"}`}
            data-testid={`${testid}-${tab.key}`}>
            <span className="block font-heading text-base font-semibold">{tab.label} <span className="font-body text-sm font-normal opacity-80">({tab.count})</span></span>
            <span className={`block text-xs ${active ? "text-white/80" : "text-slate-400"}`}>{tab.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

// L'addetto non ha un'email propria: entra con l'email aziendale + il suo PIN.
// Mostra in sola lettura quale email aziendale usera'.
export function companyAccessEmail(companyEmail, users = []) {
  return (companyEmail || "").trim() || users.find((u) => u.role !== "addetto" && u.email)?.email || "";
}

export function CompanyAccessEmail({ email, testid }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wider text-emerald-800">Email di accesso (aziendale)</div>
      {email
        ? <div className="mt-1 break-all font-medium text-navy">{email}</div>
        : <div className="mt-1 font-medium text-red-700">Email aziendale non ancora inserita: aggiungila al passo «Azienda» o crea il titolare.</div>}
      <p className="mt-1 text-xs text-slate-600">È la stessa per tutti gli addetti: ognuno si distingue solo con il suo PIN, che decide anche sedi e reparti a cui accede.</p>
    </div>
  );
}

// Azioni sull'utente gia' creato nel passo "Utenti" dell'onboarding:
// modifica dati e reparti, cambio PIN (addetto) o password (titolare), eliminazione.
// Usa gli stessi endpoint della scheda azienda.
export default function OnboardingUserActions({ tenantId, user, sites, companyEmail, accessEmail, onUpdated, onDeleted }) {
  const [panel, setPanel] = useState(null); // "edit" | "secret" | null
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState(null);
  const [secret, setSecret] = useState("");
  const [secretConfirm, setSecretConfirm] = useState("");
  const isAddetto = user.role === "addetto";
  const base = `/admin/tenants/${tenantId}/users/${user.id}`;

  const openEdit = () => {
    setDraft({ name: user.name || "", email: user.email || "", site_ids: user.site_ids || [], department_ids: user.department_ids || [] });
    setPanel("edit");
  };
  const openSecret = () => { setSecret(""); setSecretConfirm(""); setPanel("secret"); };
  const close = () => { setPanel(null); setDraft(null); setSecret(""); setSecretConfirm(""); };

  const toggleSite = (siteId, checked) => setDraft((current) => {
    const site_ids = checked ? [...current.site_ids, siteId] : current.site_ids.filter((id) => id !== siteId);
    const allowed = sites.filter((s) => site_ids.includes(s.id)).flatMap((s) => s.departments.map((d) => d.id));
    return { ...current, site_ids, department_ids: current.department_ids.filter((id) => allowed.includes(id)) };
  });
  const toggleDepartment = (depId, checked) => setDraft((current) => ({
    ...current,
    department_ids: checked ? [...current.department_ids, depId] : current.department_ids.filter((id) => id !== depId),
  }));

  const saveEdit = async () => {
    if (!draft.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    if (isAddetto && !draft.site_ids.length) { toast.error("Seleziona almeno una sede"); return; }
    if (isAddetto && !draft.department_ids.length) { toast.error("Seleziona almeno un reparto"); return; }
    const body = {
      name: draft.name.trim(), email: isAddetto ? "" : draft.email.trim(), role: user.role,
      site_ids: isAddetto ? draft.site_ids : (user.site_ids || []),
      department_ids: isAddetto ? draft.department_ids : (user.department_ids || []),
      // campi non modificabili qui: si rimandano invariati, altrimenti il server li azzererebbe
      can_upload: Boolean(user.can_upload),
      haccp_self_control_manager: Boolean(user.haccp_self_control_manager),
      is_employer: Boolean(user.is_employer),
    };
    setBusy(true);
    try {
      await api.patch(base, body);
      const siteNames = sites.filter((s) => body.site_ids.includes(s.id)).map((s) => s.name);
      const departmentNames = sites.flatMap((s) => s.departments).filter((d) => body.department_ids.includes(d.id)).map((d) => d.name);
      onUpdated({ ...user, ...body, email: body.email || undefined, site_name: siteNames.join(", "), department_names: departmentNames });
      toast.success("Dati aggiornati");
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSecret = async () => {
    if (isAddetto && !/^\d{6}$/.test(secret)) { toast.error("Il PIN deve contenere 6 cifre"); return; }
    if (!isAddetto && secret.length < 12) { toast.error("La password deve contenere almeno 12 caratteri"); return; }
    if (secret !== secretConfirm) { toast.error(isAddetto ? "I due PIN non coincidono" : "Le due password non coincidono"); return; }
    setBusy(true);
    try {
      if (isAddetto) await api.post(`${base}/reset-pin`, { pin: secret });
      else await api.post(`${base}/reset-password`, { password: secret });
      toast.success(isAddetto ? `PIN di ${user.name} cambiato` : `Password di ${user.name} cambiata`);
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(base);
      toast.success(`${user.name} eliminato`);
      setConfirmDelete(false);
      onDeleted(user);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-hairline pt-3 space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="h-10 px-3 text-xs" onClick={panel === "edit" ? close : openEdit} data-testid={`ob-user-edit-${user.id}`}><Pencil className="h-4 w-4" /> Modifica</Button>
        <Button type="button" variant="outline" className="h-10 px-3 text-xs" onClick={panel === "secret" ? close : openSecret} data-testid={`ob-user-secret-${user.id}`}><KeyRound className="h-4 w-4" /> {isAddetto ? "Cambia PIN" : "Cambia password"}</Button>
        <Button type="button" variant="outline" className="h-10 px-3 text-xs border-red-300 text-red-700 hover:bg-red-50" onClick={() => setConfirmDelete(true)} data-testid={`ob-user-delete-${user.id}`}><Trash2 className="h-4 w-4" /> Elimina</Button>
      </div>

      {panel === "edit" && draft && (
        <div className="rounded-lg border border-hairline bg-white p-3 space-y-3" data-testid={`ob-user-edit-panel-${user.id}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Nome e cognome"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
            {isAddetto ? <CompanyAccessEmail email={accessEmail} /> : <Field label="Email di accesso"><div className="flex gap-2"><Input type="email" autoComplete="off" data-preserve-case="true" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="min-w-0 flex-1" /><CopyCompanyEmailButton companyEmail={companyEmail} onCopy={(email) => setDraft({ ...draft, email })} testid={`ob-user-edit-email-copy-${user.id}`} /></div></Field>}
          </div>
          {isAddetto && (
            <div className="space-y-3">
              <div>
                <div className="mb-2 text-sm font-medium text-navy">Sedi di lavoro</div>
                <div className="flex flex-wrap gap-2">
                  {sites.map((site) => (
                    <label key={site.id} className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm ${draft.site_ids.includes(site.id) ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-hairline text-slate-600"}`}>
                      <input type="checkbox" checked={draft.site_ids.includes(site.id)} onChange={(e) => toggleSite(site.id, e.target.checked)} className="h-4 w-4 accent-emerald-600" />{site.name}
                    </label>
                  ))}
                </div>
              </div>
              {sites.filter((site) => draft.site_ids.includes(site.id)).map((site) => (
                <div key={site.id}>
                  <div className="mb-2 text-sm font-medium text-navy">Reparti · {site.name}</div>
                  <div className="flex flex-wrap gap-2">
                    {site.departments.map((dep) => (
                      <label key={dep.id} className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm ${draft.department_ids.includes(dep.id) ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-hairline text-slate-600"}`}>
                        <input type="checkbox" checked={draft.department_ids.includes(dep.id)} onChange={(e) => toggleDepartment(dep.id, e.target.checked)} className="h-4 w-4 accent-emerald-600" />{dep.name}
                      </label>
                    ))}
                    {site.departments.length === 0 && <p className="text-sm text-amber-700">La sede non contiene ancora reparti.</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="success" loading={busy} onClick={saveEdit}>Salva modifiche</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={close}>Annulla</Button>
          </div>
        </div>
      )}

      {panel === "secret" && (
        <div className="rounded-lg border border-hairline bg-white p-3 space-y-3" data-testid={`ob-user-secret-panel-${user.id}`}>
          <p className="text-xs text-slate-500">
            {isAddetto
              ? "Il PIN attuale non si può leggere (è salvato cifrato): scegline uno nuovo da 6 cifre, diverso da quello degli altri addetti. Le sessioni aperte verranno chiuse."
              : "La password attuale non si può leggere (è salvata cifrata): scegline una nuova di almeno 12 caratteri. Le sessioni aperte verranno chiuse."}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {isAddetto ? (
              <>
                <Field label="Nuovo PIN"><Input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={secret} onChange={(e) => setSecret(e.target.value.replace(/\D/g, ""))} data-testid={`ob-user-new-pin-${user.id}`} /></Field>
                <Field label="Ripeti nuovo PIN"><Input type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={secretConfirm} onChange={(e) => setSecretConfirm(e.target.value.replace(/\D/g, ""))} data-testid={`ob-user-confirm-pin-${user.id}`} /></Field>
              </>
            ) : (
              <>
                <Field label="Nuova password"><PasswordInput autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} /></Field>
                <Field label="Ripeti nuova password"><PasswordInput autoComplete="new-password" value={secretConfirm} onChange={(e) => setSecretConfirm(e.target.value)} /></Field>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="success" loading={busy} onClick={saveSecret} data-testid={`ob-user-secret-save-${user.id}`}>{isAddetto ? "Salva nuovo PIN" : "Salva nuova password"}</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={close}>Annulla</Button>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={confirmDelete}
        title={`Eliminare ${user.name}?`}
        description={<>
          <p>{user.name} non potrà più accedere all’app e le sessioni aperte verranno chiuse subito.</p>
          <p className="mt-2">Le registrazioni già fatte e la firma depositata restano conservate nello storico, come richiesto per i controlli.</p>
        </>}
        confirmLabel="Sì, elimina"
        busy={busy}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={remove}
      />
    </div>
  );
}
